// Opens a timeline episode from the episode store (episodeStore.ts), shares it with everything inside via
// `useTimeline()`, and handles undo and redo from the keyboard. The store keeps the episode and saves it, so this only shows it.
//
// A v4-or-older episode shows a plain notice page instead (it can't be opened here).
import { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ComponentType } from "react";
import type { TimelineEpisode } from "../../model/timeline";
import { Body, Page, Toast, TopBar } from "../common";
import { TimelineEditor } from "./TimelineEditor";
import { episodeView, openEpisode, redoEpisode, saveNow, subscribeEpisode, undoEpisode, updateEpisode, type EpisodeView, type Update } from "./episodeStore";

export type TimelineCtx = { ep: TimelineEpisode; update: Update<TimelineEpisode>; undo: () => void; redo: () => void; canUndo: boolean; canRedo: boolean };
const Ctx = createContext<TimelineCtx | null>(null);
export const TimelineProvider = Ctx.Provider; // the dev canvas supplies an episode without loading or saving one
export const useTimeline = () => useContext(Ctx)!;

// `onOwn`: the demo editor's "Make your own" (a demo episode's back arrow goes to the landing page, via `onBack`).
export type EditorProps = { onBack: () => void; onOwn?: () => void };

const DefaultEditor: ComponentType<EditorProps> = TimelineEditor;

export function TimelineLoader({ id, onBack, onOwn, onError, editor = DefaultEditor }: { id: string; onBack: () => void; onOwn?: () => void; onError: (m: string) => void; editor?: ComponentType<EditorProps> }) {
  // Which episode the state is for, so a new `id` shows "Loading…" until that one is open (never the old one's state).
  const [state, setState] = useState<{ id: string; is: "old" | "ready" } | null>(null);
  useEffect(() => {
    let live = true;
    openEpisode(id).then(v => { if (live) setState({ id, is: v === "old" ? "old" : "ready" }); }, e => { if (live) onError((e as Error).message); });
    return () => { live = false; };
  }, [id]);
  if (state?.id !== id) return <Page><div className="loading">Loading…</div></Page>;
  if (state.is === "old") return <OldEditorNotice onBack={onBack} />;
  return <Loaded id={id} onBack={onBack} onOwn={onOwn} Editor={editor} />;
}

function OldEditorNotice({ onBack }: { onBack: () => void }) {
  return (
    <Page>
      <TopBar onBack={onBack} />
      <Body>
        <p className="hint">This episode was made with the old editor and can't be opened here.</p>
        <button className="btn big" onClick={onBack}>Back</button>
      </Body>
    </Page>
  );
}

function Loaded({ id, onBack, onOwn, Editor }: { id: string; onBack: () => void; onOwn?: () => void; Editor: ComponentType<EditorProps> }) {
  const view = useSyncExternalStore(useCallback(l => subscribeEpisode(id, l), [id]), () => episodeView(id)) as EpisodeView;
  const update: Update = useCallback((fn, opts) => updateEpisode(id, fn, opts), [id]);
  const undo = useCallback(() => undoEpisode(id), [id]);
  const redo = useCallback(() => redoEpisode(id), [id]);
  // Closing the editor writes a change still waiting for its autosave.
  useEffect(() => () => { void saveNow(id); }, [id]);
  // Undo and redo from the keyboard, when not typing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== "z" || (e.target as HTMLElement).closest("input, textarea")) return;
      e.preventDefault();
      if (e.shiftKey) redo(); else undo();
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [undo, redo]);
  const ctx = useMemo<TimelineCtx>(() => ({ ep: view.ep, update, undo, redo, canUndo: view.canUndo, canRedo: view.canRedo }), [view, update, undo, redo]);
  return (
    <Ctx.Provider value={ctx}>
      <Editor onBack={onBack} onOwn={onOwn} />
      {view.saveError && <Toast>{view.saveError}</Toast>}
    </Ctx.Provider>
  );
}
